<template>
    <div class="message waba-template-message" :class="{ [float]: true }">
        <div class="waba-bubble">
            <div class="waba-content">
                <template v-if="meta.templateType !== 'CAROUSEL'">
                    <div class="waba-header-media" v-if="meta.headerMediaType && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(meta.headerMediaType)">
                        <img
                            v-if="meta.headerMediaType === 'IMAGE'"
                            :src="meta.headerMediaURL || getDefaultImage('IMAGE')"
                            alt=""
                            @error="onImageError"
                        />
                        <video
                            v-else-if="meta.headerMediaType === 'VIDEO'"
                            :src="meta.headerMediaURL || ''"
                            controls
                        />
                        <div v-else-if="meta.headerMediaType === 'DOCUMENT'" class="waba-document">
                            <a :href="meta.headerMediaURL" target="_blank" rel="noopener" v-if="meta.headerMediaURL">
                                <i class="material-icons">description</i>
                                <span>Document</span>
                                <i class="material-icons">download</i>
                            </a>
                            <div v-else class="waba-document-placeholder">
                                <i class="material-icons">description</i>
                                <span>Document</span>
                            </div>
                        </div>
                    </div>

                    <div class="waba-lto" v-if="meta.limitedTimeOffer && meta.limitedTimeOffer.text">
                        <div class="waba-lto-icon">
                            <i class="material-icons">card_giftcard</i>
                        </div>
                        <div class="waba-lto-body">
                            <div class="waba-lto-title">{{ meta.limitedTimeOffer.text }}</div>
                            <div class="waba-lto-expiry" v-if="meta.limitedTimeOffer.hasExpiration">
                                {{ formatLtoExpiry(meta.limitedTimeOffer.expirationDate) }}
                            </div>
                        </div>
                    </div>
                </template>

                <div class="waba-header-text" v-if="meta.headerText">{{ meta.headerText }}</div>
                <div class="waba-body" v-if="meta.body">{{ meta.body }}</div>
                <div class="waba-footer" v-if="meta.footer">{{ meta.footer }}</div>

                <div class="waba-timestamp" v-if="getCreatedDate()">
                    <span
                        v-tippy="{ content: getFullTime(), followCursor: true, placement: 'top' }"
                    >{{ getTime() }}</span>
                </div>
            </div>

            <div class="waba-buttons" v-if="meta.buttons && meta.buttons.length > 0">
                <div
                    class="waba-btn"
                    :class="getButtonClass(button)"
                    v-for="(button, i) in visibleButtons"
                    :key="'btn-' + i"
                >
                    <i class="material-icons" v-if="getButtonIcon(button)">{{ getButtonIcon(button) }}</i>
                    <span>{{ button.text }}</span>
                </div>
                <div class="waba-btn waba-btn-more" v-if="meta.buttons.length > 3" key="btn-more">
                    <i class="material-icons">list</i>
                    <span>See all options</span>
                </div>
            </div>
        </div>

        <div class="waba-carousel" ref="carouselContainer" v-if="meta.templateType === 'CAROUSEL' && meta.cards && meta.cards.length > 0">
            <div class="waba-carousel-track">
                <div class="waba-carousel-card" v-for="(card, ci) in meta.cards" :key="'card-' + ci">
                    <div class="waba-carousel-card-media">
                        <img
                            v-if="!card.headerType || card.headerType === 'IMAGE'"
                            :src="card.mediaUrl || getDefaultImage('IMAGE')"
                            alt=""
                            @error="onImageError"
                        />
                        <video v-else-if="card.headerType === 'VIDEO'" :src="card.mediaUrl || ''" controls />
                        <div v-else-if="card.headerType === 'DOCUMENT'" class="waba-document-placeholder">
                            <i class="material-icons">description</i>
                        </div>
                    </div>
                    <div class="waba-carousel-card-body" v-if="card.body || card.sampleText">
                        {{ card.body || card.sampleText }}
                    </div>
                    <div class="waba-buttons" v-if="card.buttons && card.buttons.length > 0">
                        <div
                            class="waba-btn"
                            :class="getButtonClass(btn)"
                            v-for="(btn, bi) in card.buttons"
                            :key="'card-' + ci + '-btn-' + bi"
                        >
                            <i class="material-icons" v-if="getButtonIcon(btn)">{{ getButtonIcon(btn) }}</i>
                            <span>{{ btn.text }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="waba-info-bar" v-if="infoText">
            <i class="material-icons">info_outline</i>
            <span>{{ infoText }}</span>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import { mapState } from "vuex";
import moment from "moment";

export default {
    name: "WABATemplateMessage",
    props: {
        data: {
            type: Object,
            required: true,
        },
        conversationId: {
            type: String,
        },
        float: {
            type: String,
            default: "right",
        },
        lastClickedButton: {
            type: [String, Number],
            default: null,
        },
    },
    data: function () {
        return {
            meta: {
                templateType: "TEXT",
                body: "",
                footer: "",
                headerText: "",
                headerMediaType: null,
                headerMediaURL: null,
                buttons: [],
                cards: [],
                limitedTimeOffer: null,
            },
        };
    },
    computed: {
        ...mapState({
            siteId: function (state) { return _.get(state, "site.site._id"); },
            users: function (state) { return _.get(state, "user.users.data", []); },
        }),
        infoText: function () {
            var data = this.data || {};
            if (data.fromBroadcast) return "Broadcast ile gönderildi";
            if (data.fromAPI) return "API ile gönderildi";
            if (data.fromAutomation) return "Otomasyon ile gönderildi";
            var username = data.username || "";
            if (!username && data.user) {
                var userHashMap = _.keyBy(this.users, "_id");
                if (typeof data.user === "object") {
                    username = _.get(data.user, "name", "");
                } else {
                    username = _.get(userHashMap, "[" + data.user + "].name", "");
                }
            }
            if (username) return username + " tarafından gönderildi";
            return null;
        },
        visibleButtons: function () {
            if (!this.meta.buttons) return [];
            if (this.meta.buttons.length > 3) return this.meta.buttons.slice(0, 2);
            return this.meta.buttons.slice(0, 3);
        },
    },
    created: function () {
        this.parseTemplate();
    },
    mounted: function () {
        this.scrollCarouselToEnd();
    },
    updated: function () {
        this.scrollCarouselToEnd();
    },
    watch: {
        data: {
            handler: function () { this.parseTemplate(); },
            deep: true,
        },
    },
    methods: {
        scrollCarouselToEnd: function () {
            var self = this;
            this.$nextTick(function () {
                var el = self.$refs.carouselContainer;
                if (el) {
                    el.scrollLeft = el.scrollWidth;
                }
            });
        },

        parseTemplate: function () {
            var payload = this.data.payload || this.data;
            if (!payload.params && this.data.params) {
                payload = Object.assign({}, payload, { params: this.data.params });
            }
            var result;
            if (this.isMetaFormat(payload)) {
                result = this.parseMetaTemplate(payload);
            } else {
                result = this.parseGupshupTemplate(payload);
            }
            this.meta = result;
        },

        isMetaFormat: function (template) {
            return template.format === "meta" || (template.components && Array.isArray(template.components));
        },

        parseMetaTemplate: function (template) {
            var components = template.components || [];
            var params = _.get(template, "params", {}) || {};
            var headerParams = [];
            var bodyParams = [];
            var buttonParams = [];
            var carouselParams = _.get(params, "carousel", []);

            if (Array.isArray(params)) {
                bodyParams = params;
            } else {
                headerParams = params.header || [];
                bodyParams = params.body || [];
                buttonParams = params.button || [];
            }

            var headerComponent = components.find(function (c) { return c.type === "HEADER"; });
            var bodyComponent = components.find(function (c) { return c.type === "BODY"; });
            var footerComponent = components.find(function (c) { return c.type === "FOOTER"; });
            var buttonsComponent = components.find(function (c) { return c.type === "BUTTONS"; });
            var carouselComponent = components.find(function (c) { return c.type === "CAROUSEL"; });
            var ltoComponent = components.find(function (c) { return c.type === "LIMITED_TIME_OFFER"; });

            var headerText = "";
            var headerMediaType = null;
            var headerMediaURL = null;

            if (headerComponent) {
                if (headerComponent.format === "TEXT") {
                    headerText = headerComponent.text || "";
                    if (headerParams.length > 0) {
                        var hpi = 0;
                        headerText = headerText.replace(/{{\d+}}/g, function () {
                            var val = headerParams[hpi] || "";
                            hpi++;
                            return val;
                        });
                    }
                } else if (["IMAGE", "VIDEO", "DOCUMENT"].indexOf(headerComponent.format) !== -1) {
                    headerMediaType = headerComponent.format;
                    if (headerParams.length > 0 && headerParams[0] && String(headerParams[0]).trim()) {
                        headerMediaURL = headerParams[0];
                    } else {
                        headerMediaURL = _.get(headerComponent, "example.header_handle[0]") ||
                            _.get(template, "mediaPayload.headerAsset.cdnUrl") ||
                            _.get(template, "asset.url") ||
                            _.get(template, "asset.cdnUrl");
                    }
                }
            }

            var body = _.get(bodyComponent, "text", "") || "";
            if (bodyParams.length > 0) {
                var bpi = 0;
                body = body.replace(/{{\d+}}/g, function () {
                    var val = bodyParams[bpi] || "";
                    bpi++;
                    return val;
                });
            }

            var footer = _.get(footerComponent, "text", "") || "";

            var buttons = [];
            if (buttonsComponent && buttonsComponent.buttons) {
                var btnParamsCopy = (buttonParams || []).slice();
                buttons = buttonsComponent.buttons.map(function (btn) {
                    var obj = { text: btn.text || "", type: btn.type };
                    if (btn.type === "URL") {
                        var url = btn.url || "";
                        if (url.indexOf("{{") !== -1 && btnParamsCopy.length > 0) {
                            url = url.replace(/{{\d+}}/g, function () {
                                return btnParamsCopy.shift() || "";
                            });
                        }
                        obj.url = url;
                    } else if (btn.type === "PHONE_NUMBER") {
                        obj.phone_number = btn.phone_number;
                    }
                    return obj;
                });
            }

            var cards = [];
            var templateType = carouselComponent ? "CAROUSEL" : "TEXT";

            if (carouselComponent && carouselComponent.cards) {
                cards = carouselComponent.cards.map(function (card, cardIdx) {
                    var cardComps = card.components || [];
                    var cardHeader = cardComps.find(function (c) { return c.type === "HEADER"; });
                    var cardBody = cardComps.find(function (c) { return c.type === "BODY"; });
                    var cardButtons = cardComps.find(function (c) { return c.type === "BUTTONS"; });

                    var headerType = cardHeader ? cardHeader.format : "IMAGE";
                    var mediaUrl = _.get(cardHeader, "example.header_handle[0]") ||
                        _.get(template, "mediaPayload.cardAssets[" + cardIdx + "].cdnUrl");

                    var cp = carouselParams[cardIdx] || {};
                    if (cp.header && cp.header[0] && String(cp.header[0]).trim()) {
                        mediaUrl = cp.header[0];
                    }

                    var cardBodyText = _.get(cardBody, "text", "") || "";
                    if (cp.body && cp.body.length > 0) {
                        var cbi = 0;
                        cardBodyText = cardBodyText.replace(/{{\d+}}/g, function () {
                            var val = cp.body[cbi] || "";
                            cbi++;
                            return val;
                        });
                    }

                    var cardBtns = [];
                    if (cardButtons && cardButtons.buttons) {
                        var cbtnParams = (cp.button || []).slice();
                        cardBtns = cardButtons.buttons.map(function (btn) {
                            var o = { text: btn.text || "", type: btn.type };
                            if (btn.type === "URL") {
                                var u = btn.url || "";
                                if (u.indexOf("{{") !== -1 && cbtnParams.length > 0) {
                                    u = u.replace(/{{\d+}}/g, function () {
                                        return cbtnParams.shift() || "";
                                    });
                                }
                                o.url = u;
                            }
                            return o;
                        });
                    }

                    return {
                        headerType: headerType,
                        mediaUrl: mediaUrl,
                        body: cardBodyText,
                        buttons: cardBtns,
                    };
                });
            }

            var limitedTimeOffer = null;
            if (ltoComponent) {
                limitedTimeOffer = {
                    text: ltoComponent.text || "",
                    hasExpiration: ltoComponent.has_expiration !== false,
                    expirationDate: ltoComponent.expiration_time_ms || null,
                };
            }

            return {
                templateType: templateType,
                headerText: headerText,
                headerMediaType: headerMediaType,
                headerMediaURL: headerMediaURL,
                body: body,
                footer: footer,
                buttons: buttons,
                cards: cards,
                limitedTimeOffer: limitedTimeOffer,
            };
        },

        parseGupshupTemplate: function (template) {
            var metaObject = {};
            var containerMetaObj = {};
            try { metaObject = JSON.parse(template.meta || "{}"); } catch (e) { metaObject = {}; }
            try { containerMetaObj = JSON.parse(template.containerMeta || "{}"); } catch (e) { containerMetaObj = {}; }

            var params = _.cloneDeep(_.get(template, "params", []) || []);
            if (typeof params === "string") params = [params];
            if (!Array.isArray(params)) params = [];

            var bodyFromContainerMeta = _.get(containerMetaObj, "data");
            var body = bodyFromContainerMeta || _.get(metaObject, "example") || metaObject.data || "";
            var headerText = _.get(containerMetaObj, "header", "") || "";
            var footer = _.get(containerMetaObj, "footer", "") || "";

            var buttons = _.get(containerMetaObj, "buttons") || template.buttons || [];
            var cards = _.cloneDeep(_.get(containerMetaObj, "cards", []) || []);

            if (params.length > 0 && headerText) {
                headerText = headerText.replace(/{{\d+}}/g, function () {
                    if (params.length === 0) return "";
                    return params.shift() || "";
                });
            }

            if (params.length > 0 && body) {
                body = body.replace(/{{\d+}}/g, function () {
                    if (params.length === 0) return "";
                    return params.shift() || "";
                });
            }

            if (params.length > 0 && cards.length > 0) {
                cards = cards.map(function (card) {
                    if (card.body) {
                        card.body = card.body.replace(/{{\d+}}/g, function () {
                            if (params.length === 0) return "";
                            return params.shift() || "";
                        });
                    }
                    return card;
                });
            }

            if (params.length > 0 && Array.isArray(buttons) && buttons.length > 0) {
                buttons = buttons.map(function (button) {
                    if (button.type === "URL" && button.url) {
                        button.url = button.url.replace(/{{\d+}}/g, function () {
                            if (params.length === 0) return "";
                            return params.shift() || "";
                        });
                    }
                    return button;
                });
            }

            var headerMediaType = null;
            var headerMediaURL = null;
            var templateType = template.templateType || "TEXT";

            if (["IMAGE", "VIDEO", "DOCUMENT"].indexOf(templateType) !== -1) {
                headerMediaType = templateType;
                headerMediaURL = _.get(template, "asset.url") || _.get(template, "asset.cdnUrl") || null;
                templateType = "TEXT";
            }

            return {
                templateType: templateType,
                headerText: headerText,
                headerMediaType: headerMediaType,
                headerMediaURL: headerMediaURL,
                body: body,
                footer: footer,
                buttons: Array.isArray(buttons) ? buttons : [],
                cards: cards,
                limitedTimeOffer: null,
            };
        },

        getDefaultImage: function (type) {
            var map = {
                IMAGE: "https://static.supsis.live/static/image-preview.svg",
                VIDEO: "https://static.supsis.live/static/video-preview.svg",
                DOCUMENT: "https://static.supsis.live/static/pdf-preview.svg",
            };
            return map[type] || map.IMAGE;
        },

        onImageError: function (e) {
            e.target.src = this.getDefaultImage("IMAGE");
        },

        getButtonClass: function (button) {
            if (!button || !button.type) return "waba-btn-reply";
            switch (button.type) {
                case "URL": return "waba-btn-url";
                case "PHONE_NUMBER": return "waba-btn-phone";
                case "COPY_CODE": return "waba-btn-copy";
                default: return "waba-btn-reply";
            }
        },

        getButtonIcon: function (button) {
            if (!button || !button.type) return null;
            switch (button.type) {
                case "URL": return "open_in_new";
                case "PHONE_NUMBER": return "call";
                case "COPY_CODE": return "content_copy";
                default: return null;
            }
        },

        formatLtoExpiry: function (date) {
            if (!date) return "Limited time offer";
            if (typeof date === "string" && date.startsWith("[")) return "Expires: " + date;
            return "Expires: " + moment(date).format("MMM DD, YYYY HH:mm");
        },

        getCreatedDate: function () {
            return _.get(this.data, "createdDate") || _.get(this.data, "createdAt") || null;
        },

        getTime: function () {
            var date = this.getCreatedDate();
            if (!date) return "";
            return moment(date).format("HH:mm");
        },

        getFullTime: function () {
            var date = this.getCreatedDate();
            if (!date) return "";
            return moment(date).format("DD MMMM YYYY, HH:mm:ss");
        },
    },
};
</script>

<style lang="scss" scoped>
.message.waba-template-message {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &.left {
        align-items: flex-start;
    }

    &.right {
        align-items: flex-end;
    }

    .waba-bubble {
        width: 300px;
        border-radius: 8px;
        overflow: hidden;
        background: #fff;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }

    .waba-info-bar {
        display: flex;
        align-items: center;
        gap: 3px;
        padding: 2px 4px;
        color: #8696a0;
        font-size: 10px;
        line-height: 1.2;

        .material-icons {
            font-size: 12px;
            flex-shrink: 0;
        }

        span {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    .waba-content {
        padding: 0;
    }

    .waba-header-media {
        width: 100%;
        max-height: 220px;
        overflow: hidden;

        img {
            width: 100%;
            height: auto;
            max-height: 220px;
            object-fit: cover;
            display: block;
        }

        video {
            width: 100%;
            max-height: 220px;
            display: block;
        }
    }

    .waba-document {
        a, .waba-document-placeholder {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 14px 12px;
            background: #f0f2f5;
            color: #54656f;
            text-decoration: none;
            font-size: 13px;

            .material-icons:first-child {
                font-size: 28px;
                color: #8696a0;
            }

            span {
                flex: 1;
                font-weight: 500;
            }

            .material-icons:last-child {
                font-size: 20px;
                color: #8696a0;
            }
        }
    }

    .waba-lto {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        margin: 0 8px 4px;
        background: #f5f5f5;
        border-radius: 8px;
        border: 1px solid #e8e8e8;
        margin-top: 8px;

        .waba-lto-icon {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: linear-gradient(135deg, #95de64, #73d13d);
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;

            .material-icons {
                font-size: 20px;
                color: #fff;
            }
        }

        .waba-lto-body {
            flex: 1;
            min-width: 0;

            .waba-lto-title {
                font-weight: 600;
                font-size: 13px;
                color: #262626;
            }

            .waba-lto-expiry {
                font-size: 12px;
                color: #8c8c8c;
                margin-top: 1px;
            }
        }
    }

    .waba-header-text {
        padding: 8px 10px 0;
        font-size: 15px;
        font-weight: 600;
        color: #111b21;
        overflow-wrap: break-word;
        word-break: break-word;
    }

    .waba-body {
        padding: 6px 10px 4px;
        font-size: 13.5px;
        color: #111b21;
        line-height: 1.45;
        white-space: pre-line;
        overflow-wrap: break-word;
        word-break: break-word;
    }

    .waba-footer {
        padding: 2px 10px 4px;
        font-size: 12px;
        color: #8696a0;
        line-height: 1.3;
        overflow-wrap: break-word;
        word-break: break-word;
    }

    .waba-timestamp {
        padding: 0 10px 6px;
        text-align: right;
        font-size: 11px;
        color: #8696a0;
        cursor: default;
    }

    .waba-buttons {
        display: flex;
        flex-direction: column;

        .waba-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            padding: 10px 12px;
            border-top: 1px solid #e9edef;
            color: #00a5f4;
            font-size: 13px;
            font-weight: 500;
            cursor: default;
            text-align: center;
            transition: background 0.15s;

            .material-icons {
                font-size: 18px;
            }

            span {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            &.waba-btn-copy {
                color: #54656f;
                background: #f0f2f5;
                font-weight: 600;
            }
        }
    }

    .waba-carousel {
        max-width: 500px;
        overflow-x: auto;
        padding-bottom: 4px;

        &::-webkit-scrollbar {
            height: 4px;
        }

        &::-webkit-scrollbar-thumb {
            background: #c5c5c5;
            border-radius: 4px;
        }

        .waba-carousel-track {
            display: flex;
            gap: 8px;
            width: max-content;
        }

        .waba-carousel-card {
            width: 220px;
            min-width: 220px;
            background: #fff;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
            display: flex;
            flex-direction: column;

            .waba-carousel-card-media {
                width: 100%;
                height: 160px;
                overflow: hidden;
                background: #f0f2f5;

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    display: block;
                }

                video {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    display: block;
                }

                .waba-document-placeholder {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                    color: #8696a0;

                    .material-icons {
                        font-size: 40px;
                    }
                }
            }

            .waba-carousel-card-body {
                padding: 8px 10px;
                font-size: 13px;
                color: #111b21;
                line-height: 1.4;
                white-space: pre-line;
                overflow-wrap: break-word;
                word-break: break-word;
                flex: 1;
            }

            .waba-buttons {
                .waba-btn {
                    font-size: 12px;
                    padding: 8px 10px;
                }
            }
        }
    }
}
</style>
